import React, {useEffect, useMemo, useState} from 'react'
import {Chart} from "primereact/chart";
import FetchUtil from "../../../../util/FetchUtil";
import ConfigUtil from "../../../../util/ConfigUtil";
import {FARBEN, getDiagrammOptionen, useSchmaleAnsicht, vorhanden, zahl} from "./statistikWerte";

// über so viele Spielerwerte wird der Trend des Events gemittelt
const TREND_FENSTER = 10;
const FARBE_TREND = "#e6c14a";
const FARBE_GEDIMMT = "rgba(155, 183, 194, 0.3)";

// auswählbare Werte; bei den Legs sind weniger Darts besser, die Achse wird dafür umgedreht (besser = oben).
// ohneDurchschnitt: weder Trendlinie noch Ø-Kennzahl, ein Durchschnitt der Leg-Darts ist nicht aussagekräftig
const WERTE = [
    {id: "average", label: "Average", titel: "Average", nachkomma: 2},
    {id: "first9Average", label: "First 9", titel: "First 9 Average", nachkomma: 2},
    {id: "bestLeg", label: "Best Leg", titel: "Best Leg", einheit: "Darts", nachkomma: 0, kleinerBesser: true, ohneDurchschnitt: true},
    {id: "worstLeg", label: "Worst Leg", titel: "Worst Leg", einheit: "Darts", nachkomma: 0, kleinerBesser: true, ohneDurchschnitt: true},
];

/**
 * Statistik "Verlauf": je Spiel der Wert beider Spieler (Average, First 9 Average, Best oder Worst Leg)
 * zum Startzeitpunkt des Spiels, damit sieht man die Entwicklung über das ganze Event.
 *
 * @param aktualisierung ändert sich, sobald ein Spiel beendet wurde, dann werden die Daten neu geladen
 * @param markierterSpieler aus der Spielersuche der Seite, wird im Diagramm hervorgehoben
 */
export default function VerlaufBereich({tournamentId, aktualisierung, markierterSpieler}) {

    const [spiele, setSpiele] = useState(undefined);
    const [fehler, setFehler] = useState(undefined);
    const [wertId, setWertId] = useState("average");
    // undefined: Spieler aus der Spielersuche, "" alle Spieler
    const [auswahl, setAuswahl] = useState(undefined);
    const schmal = useSchmaleAnsicht();

    useEffect(() => {
        FetchUtil.fetchGet(ConfigUtil.getConfig().resourceUrls.public + "/tournamentverlauf/" + tournamentId,
            json => {
                setFehler(undefined);
                setSpiele(json);
            },
            responseNotOk => setFehler(responseNotOk.message),
            error => setFehler(error.message));
    }, [tournamentId, aktualisierung]);

    const wert = WERTE.find(w => w.id === wertId);

    // je Spiel ein Punkt pro Spieler, fehlende Werte (z.B. kein Leg gewonnen, nur Ergebnis eingegeben) entfallen
    const punkte = useMemo(() => {
        const liste = [];
        for (const spiel of (spiele || [])) {
            const zeit = new Date(spiel.startzeitpunkt).getTime();
            const eintragen = (name, werte, gegner) => {
                const y = werte ? werte[wertId] : undefined;
                if (vorhanden(y) && Number(y) > 0) {
                    liste.push({x: zeit, y: Number(y), name: name || "Unbekannt", gegner: gegner || "Unbekannt"});
                }
            };
            eintragen(spiel.spieler1Name, spiel.spieler1, spiel.spieler2Name);
            eintragen(spiel.spieler2Name, spiel.spieler2, spiel.spieler1Name);
        }
        return liste;
    }, [spiele, wertId]);

    // Spieler über alle Spiele, damit die Auswahl beim Wechsel des Werts erhalten bleibt
    const namen = useMemo(() => {
        const alle = new Set();
        for (const spiel of (spiele || [])) {
            [spiel.spieler1Name, spiel.spieler2Name].filter(vorhanden).forEach(name => alle.add(name));
        }
        return [...alle].sort((a, b) => a.localeCompare(b));
    }, [spiele]);

    if (fehler !== undefined) {
        return <div className="dhpt-statistik"><p className="dhpt-hinweis" role="alert">Der Verlauf konnte nicht geladen werden: {fehler}</p></div>
    }
    if (spiele === undefined) {
        return <div className="dhpt-statistik"><p className="dhpt-hinweis"><i className="pi pi-spin pi-spinner" aria-hidden="true"/> Verlauf wird geladen …</p></div>
    }
    if (spiele.length === 0) {
        return <div className="dhpt-statistik">
            <p className="dhpt-hinweis">Es sind noch keine beendeten Spiele mit Statistik vorhanden. Der Verlauf erscheint, sobald die ersten Spiele gescort wurden.</p>
        </div>
    }

    const ausSuche = vorhanden(markierterSpieler) && namen.includes(markierterSpieler) ? markierterSpieler : "";
    const ausgewaehlt = auswahl === undefined ? ausSuche : auswahl;

    const alleZeiten = spiele.map(spiel => new Date(spiel.startzeitpunkt).getTime());
    const mehrereTage = new Date(Math.min(...alleZeiten)).toDateString() !== new Date(Math.max(...alleZeiten)).toDateString();
    const zeitText = (ms) => {
        const datum = new Date(ms);
        const uhrzeit = datum.toLocaleTimeString("de-DE", {hour: "2-digit", minute: "2-digit"});
        return mehrereTage ? datum.toLocaleDateString("de-DE", {day: "2-digit", month: "2-digit"}) + " " + uhrzeit : uhrzeit;
    };
    const wertText = (y) => zahl(y, wert.nachkomma) + (wert.einheit ? " " + wert.einheit : "");

    const getDiagramm = () => {
        if (punkte.length === 0) {
            return <p className="dhpt-hinweis">Für „{wert.titel}“ sind noch keine Werte vorhanden.</p>
        }
        // gleitender Durchschnitt über die letzten Spielerwerte, zeigt die Tendenz des ganzen Events
        const trend = punkte.map((punkt, index) => {
            const fenster = punkte.slice(Math.max(0, index - TREND_FENSTER + 1), index + 1);
            return {x: punkt.x, y: fenster.reduce((summe, p) => summe + p.y, 0) / fenster.length};
        });

        const datasets = [];
        if (ausgewaehlt !== "") {
            datasets.push({
                label: ausgewaehlt, data: punkte.filter(p => p.name === ausgewaehlt), showLine: true,
                borderColor: FARBEN.spielerA, backgroundColor: FARBEN.spielerA, pointRadius: 6, pointHoverRadius: 8, borderWidth: 2, order: 0,
            });
            datasets.push({
                label: "Andere Spieler", data: punkte.filter(p => p.name !== ausgewaehlt),
                backgroundColor: FARBE_GEDIMMT, borderColor: FARBE_GEDIMMT, pointRadius: 4, order: 2,
            });
        } else {
            datasets.push({
                label: wert.titel + " eines Spielers im Spiel", data: punkte,
                backgroundColor: "rgba(79, 179, 217, 0.75)", borderColor: "rgba(79, 179, 217, 0.75)", pointRadius: 4, pointHoverRadius: 6, order: 1,
            });
        }
        if (!wert.ohneDurchschnitt) {
            datasets.push({
                label: "Trend (Ø der letzten " + TREND_FENSTER + ")", data: trend, showLine: true, pointRadius: 0, pointHitRadius: 0,
                borderColor: FARBE_TREND, backgroundColor: FARBE_TREND, borderWidth: 2, borderDash: [6, 4], tension: 0.3, order: 3,
            });
        }

        const options = getDiagrammOptionen({
            scales: {
                x: {
                    type: "linear",
                    ticks: {color: FARBEN.textLeise, callback: (ms) => zeitText(ms), maxTicksLimit: schmal ? 4 : 8},
                    grid: {color: FARBEN.linie},
                    title: {display: !schmal, text: "Startzeitpunkt des Spiels", color: FARBEN.textLeise},
                },
                y: {
                    reverse: wert.kleinerBesser === true,
                    ticks: {color: FARBEN.textLeise, precision: wert.nachkomma === 0 ? 0 : undefined},
                    grid: {color: FARBEN.linie},
                    title: {display: true, text: wert.kleinerBesser ? "Darts (weniger ist besser)" : wert.titel, color: FARBEN.textLeise},
                },
            },
            plugins: {
                legend: {position: schmal ? "bottom" : "top", labels: {color: FARBEN.text, boxWidth: 12, usePointStyle: true}},
                tooltip: {
                    callbacks: {
                        title: (eintraege) => eintraege.length > 0 ? zeitText(eintraege[0].parsed.x) : "",
                        label: (kontext) => {
                            const punkt = kontext.raw;
                            if (punkt.name === undefined) {
                                return "Trend: " + zahl(punkt.y, wert.nachkomma === 0 ? 1 : 2);
                            }
                            return punkt.name + ": " + wertText(punkt.y) + " gegen " + punkt.gegner;
                        },
                    },
                },
            },
        });

        return <>
            {getKennzahlen()}
            <div className="dhpt-vl-diagramm" role="img"
                 aria-label={"Verlauf " + wert.titel + " von " + zeitText(punkte[0].x) + " bis " + zeitText(punkte[punkte.length - 1].x)
                     + (ausgewaehlt !== "" ? ", hervorgehoben: " + ausgewaehlt : "")}>
                {/* neu aufbauen bei Wechsel des Werts, sonst übernimmt Chart.js die umgedrehte Achse nicht zuverlässig */}
                <Chart key={wertId + (schmal ? "-schmal" : "-breit")} type="scatter" data={{datasets}} options={options} style={{height: "100%"}}/>
            </div>
        </>
    }

    const getKennzahlen = () => {
        const werte = ausgewaehlt !== "" ? punkte.filter(p => p.name === ausgewaehlt) : punkte;
        if (werte.length === 0) {
            return <p className="dhpt-hinweis">Für {ausgewaehlt} ist bei „{wert.titel}“ noch kein Wert vorhanden.</p>
        }
        const bester = werte.reduce((top, p) => (top === undefined || (wert.kleinerBesser ? p.y < top.y : p.y > top.y) ? p : top), undefined);
        const schnitt = werte.reduce((summe, p) => summe + p.y, 0) / werte.length;
        const kennzahl = (inhalt, bezeichnung) => <div className="dhpt-kennzahl"><b>{inhalt}</b><small>{bezeichnung}</small></div>;
        return <div className="dhpt-kennzahlen">
            {kennzahl(werte.length, "Werte im Verlauf")}
            {!wert.ohneDurchschnitt && kennzahl(zahl(schnitt, wert.nachkomma === 0 ? 1 : 2), "Ø " + wert.titel + " je Spiel")}
            {kennzahl(wertText(bester.y), "Bester Wert" + (ausgewaehlt === "" ? " · " + bester.name : ""))}
            {kennzahl(zeitText(werte[0].x) + " – " + zeitText(werte[werte.length - 1].x), "Zeitraum")}
        </div>
    }

    return <div className="dhpt-statistik">
        <section className="dhpt-karte">
            <h3 className="dhpt-karte-titel"><span>Verlauf</span><span className="dhpt-karte-zusatz">je Spiel und Spieler zum Startzeitpunkt</span></h3>
            <div className="dhpt-vl-steuerung">
                <div className="dhpt-segment" role="radiogroup" aria-label="Angezeigter Wert">
                    {WERTE.map(w => <button key={w.id} type="button" role="radio" aria-checked={wertId === w.id}
                                            className="dhpt-segment-knopf" onClick={() => setWertId(w.id)}>
                        {w.label}
                    </button>)}
                </div>
                <label className="dhpt-vl-auswahl">
                    <span>Spieler hervorheben</span>
                    <select value={ausgewaehlt} onChange={(event) => setAuswahl(event.target.value)}>
                        <option value="">Alle Spieler</option>
                        {namen.map(name => <option key={name} value={name}>{name}</option>)}
                    </select>
                </label>
            </div>
            {getDiagramm()}
            <p className="dhpt-hinweis dhpt-vl-hinweis">
                Jeder Punkt ist der Wert eines Spielers in einem Spiel. Best und Worst Leg zählen nur gewonnene Legs.
                Spiele ohne Startzeitpunkt oder ohne Scoring mit Darthelfer sind nicht enthalten.
            </p>
        </section>
    </div>
}
